// Smart Changelog Detection, Auto-Generation & AI-Powered Enrichment
// Issue #33

import type { Commit, RepoConfig, ChangelogIntegrationEvent } from './types';
import { proxiedFetch } from './cors-proxy';

// ==================== Types ====================

export type ChangelogFileCandidate =
  | 'CHANGELOG.md'
  | 'CHANGELOG.txt'
  | 'HISTORY.md'
  | 'RELEASES.md'
  | 'CHANGES.md'
  | 'NEWS.md';

export const CHANGELOG_CANDIDATES: ChangelogFileCandidate[] = [
  'CHANGELOG.md',
  'CHANGELOG.txt',
  'HISTORY.md',
  'RELEASES.md',
  'CHANGES.md',
  'NEWS.md',
];

export interface ChangelogDetectionResult {
  found: boolean;
  filename?: ChangelogFileCandidate;
  content?: string;
  source?: 'github' | 'bitbucket' | 'gitlab' | 'unknown';
  fetchedAt: string;
  error?: string;
}

export type ConventionalCommitType =
  | 'feat' | 'fix' | 'docs' | 'chore' | 'refactor'
  | 'perf' | 'test' | 'build' | 'ci' | 'style'
  | 'revert' | 'breaking' | 'other';

export const CC_TYPE_TO_SECTION: Record<ConventionalCommitType, string> = {
  feat:     'Added',
  fix:      'Fixed',
  docs:     'Changed',
  chore:    'Changed',
  refactor: 'Changed',
  perf:     'Changed',
  test:     'Changed',
  build:    'Changed',
  ci:       'Changed',
  style:    'Changed',
  revert:   'Removed',
  breaking: 'Security',
  other:    'Changed',
};

export interface ClassifiedCommit {
  commit: Commit;
  type: ConventionalCommitType;
  scope?: string;
  subject: string;
  isBreaking: boolean;
}

export interface ChangelogGap {
  commit: Commit;
  reason: 'not_mentioned';
  suggestedSection: string;
  suggestedEntry: string;
}

export interface ChangelogHealthScore {
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  coverage: number;
  recency: number;
  structure: number;
  consistency: number;
  summary: string;
}

export interface GeneratedChangelog {
  markdown: string;
  commitCount: number;
  versionCount: number;
  generatedAt: string;
}

export type AiProvider = 'openai' | 'anthropic' | 'custom';

export interface AiSettings {
  id: string;
  provider: AiProvider;
  apiKey: string;
  model?: string;
  customEndpoint?: string;
  updatedAt: string;
}

export interface AiEnrichmentResult {
  markdown: string;
  model?: string;
  enrichedAt: string;
}

export const AI_MODEL_OPTIONS: Record<AiProvider, string[]> = {
  openai:    ['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  anthropic: ['claude-3-5-haiku-20241022', 'claude-3-5-sonnet-20241022', 'claude-3-opus-20240229'],
  custom:    [],
};

// ==================== URL Parsers ====================

function parseGitHubUrl(url: string): { owner: string; repo: string } | null {
  const m = url.match(/github\.com[/:]([^/]+)\/([^/.\s]+)/i);
  if (!m) return null;
  return { owner: m[1], repo: m[2].replace(/\.git$/, '') };
}

function parseBitbucketUrl(url: string): { workspace: string; repo: string } | null {
  const m = url.match(/bitbucket\.org[/:]([^/]+)\/([^/.\s]+)/i);
  if (!m) return null;
  return { workspace: m[1], repo: m[2].replace(/\.git$/, '') };
}

function parseGitLabUrl(url: string): { instance: string; path: string } | null {
  const m = url.match(/^https?:\/\/([^/]+)\/(.+?)(?:\.git)?$/i);
  if (!m || m[1] === 'github.com' || m[1] === 'bitbucket.org') return null;
  return { instance: m[1], path: m[2] };
}

async function fetchRaw(url: string, headers: Record<string, string> = {}): Promise<string | null> {
  try {
    const resp = await proxiedFetch(url, { headers });
    if (!resp.ok) return null;
    return await resp.text();
  } catch {
    return null;
  }
}

// ==================== Provider-specific Fetchers ====================

async function fetchGitHubFile(owner: string, repo: string, path: string, token?: string): Promise<string | null> {
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // Try raw endpoint first (faster)
  const raw = await fetchRaw(`https://raw.githubusercontent.com/${owner}/${repo}/HEAD/${path}`, headers);
  if (raw !== null) return raw;

  // Fall back to API
  const apiHeaders = { ...headers, Accept: 'application/vnd.github.v3+json' };
  try {
    const resp = await proxiedFetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`, { headers: apiHeaders });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data.encoding === 'base64') return atob(data.content.replace(/\n/g, ''));
  } catch { /* */ }
  return null;
}

async function fetchBitbucketFile(workspace: string, repo: string, path: string, username?: string, password?: string): Promise<string | null> {
  const headers: Record<string, string> = {};
  if (username && password) headers['Authorization'] = `Basic ${btoa(`${username}:${password}`)}`;
  return fetchRaw(`https://api.bitbucket.org/2.0/repositories/${workspace}/${repo}/src/HEAD/${path}`, headers);
}

async function fetchGitLabFile(instance: string, projectPath: string, path: string, token?: string): Promise<string | null> {
  const headers: Record<string, string> = {};
  if (token) headers['PRIVATE-TOKEN'] = token;
  const url = `https://${instance}/api/v4/projects/${encodeURIComponent(projectPath)}/repository/files/${encodeURIComponent(path)}/raw?ref=HEAD`;
  return fetchRaw(url, headers);
}

// ==================== Changelog Detection ====================

export async function fetchRepoChangelog(config: RepoConfig): Promise<ChangelogDetectionResult> {
  const url = config.url || '';
  const fetchedAt = new Date().toISOString();

  if (config.type === 'github') {
    const parsed = parseGitHubUrl(url);
    if (!parsed) return { found: false, fetchedAt, error: 'Could not parse GitHub URL' };
    for (const candidate of CHANGELOG_CANDIDATES) {
      const content = await fetchGitHubFile(parsed.owner, parsed.repo, candidate, config.accessToken);
      if (content !== null) return { found: true, filename: candidate, content, source: 'github', fetchedAt };
    }
    return { found: false, fetchedAt, source: 'github', error: 'No changelog file found in repository root' };
  }

  if (config.type === 'bitbucket') {
    const parsed = parseBitbucketUrl(url);
    if (!parsed) return { found: false, fetchedAt, error: 'Could not parse Bitbucket URL' };
    for (const candidate of CHANGELOG_CANDIDATES) {
      const content = await fetchBitbucketFile(parsed.workspace, parsed.repo, candidate, config.username, config.appPassword);
      if (content !== null) return { found: true, filename: candidate, content, source: 'bitbucket', fetchedAt };
    }
    return { found: false, fetchedAt, source: 'bitbucket', error: 'No changelog file found in repository root' };
  }

  if (config.type === 'gitlab') {
    const parsed = parseGitLabUrl(url);
    if (!parsed) return { found: false, fetchedAt, error: 'Could not parse GitLab URL' };
    for (const candidate of CHANGELOG_CANDIDATES) {
      const content = await fetchGitLabFile(parsed.instance, parsed.path, candidate, config.accessToken);
      if (content !== null) return { found: true, filename: candidate, content, source: 'gitlab', fetchedAt };
    }
    return { found: false, fetchedAt, source: 'gitlab', error: 'No changelog file found in repository root' };
  }

  return { found: false, fetchedAt, error: 'Unsupported repository type for changelog detection' };
}

// ==================== Conventional Commit Classification ====================

const CC_PATTERN = /^(feat|fix|docs|chore|refactor|perf|test|build|ci|style|revert)(\(([^)]+)\))?(!)?\s*:\s*(.+)$/i;

export function classifyCommit(commit: Commit): ClassifiedCommit {
  const m = commit.message.match(CC_PATTERN);
  const hasBreakingNote = commit.message.includes('BREAKING CHANGE');
  const isBreaking = hasBreakingNote || (m ? m[4] === '!' : false);

  if (m) {
    return {
      commit,
      type: isBreaking ? 'breaking' : (m[1].toLowerCase() as ConventionalCommitType),
      scope: m[3],
      subject: m[5].trim(),
      isBreaking,
    };
  }

  // Heuristic fallback
  const lower = commit.message.toLowerCase();
  if (lower.startsWith('fix') || lower.startsWith('bug') || lower.includes('hotfix'))
    return { commit, type: 'fix', subject: commit.message, isBreaking };
  if (lower.startsWith('feat') || lower.startsWith('add') || lower.startsWith('new'))
    return { commit, type: 'feat', subject: commit.message, isBreaking };
  if (lower.startsWith('doc') || lower.startsWith('readme'))
    return { commit, type: 'docs', subject: commit.message, isBreaking };
  if (lower.startsWith('revert'))
    return { commit, type: 'revert', subject: commit.message, isBreaking };

  return { commit, type: 'other', subject: commit.message, isBreaking };
}

// ==================== Auto-Generate Changelog ====================

function weekStart(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
}

export function generateChangelogFromCommits(commits: Commit[], repoName?: string): GeneratedChangelog {
  if (commits.length === 0) {
    return { markdown: '# Changelog\n\nNo commits found.\n', commitCount: 0, versionCount: 0, generatedAt: new Date().toISOString() };
  }

  const sorted = [...commits]
    .filter(c => !c.isMerge && !c.message.toLowerCase().startsWith('merge '))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Group by week
  const byWeek = new Map<string, Commit[]>();
  for (const c of sorted) {
    const key = weekStart(c.date);
    if (!byWeek.has(key)) byWeek.set(key, []);
    byWeek.get(key)!.push(c);
  }
  const weeks = [...byWeek.keys()].sort((a, b) => b.localeCompare(a));

  const lines: string[] = ['# Changelog', ''];
  if (repoName) {
    lines.push(`All notable changes to **${repoName}** are documented here.`);
    lines.push('Generated automatically from Git commit history.');
    lines.push('');
  }

  const SECTION_ORDER = ['Breaking Changes', 'Added', 'Fixed', 'Changed', 'Removed'];

  for (const week of weeks) {
    const weekCommits = byWeek.get(week)!;
    const endDate = new Date(week);
    endDate.setDate(endDate.getDate() + 6);
    lines.push(`## ${week} — Week of ${week} to ${endDate.toISOString().split('T')[0]}`);
    lines.push('');

    const classified = weekCommits.map(classifyCommit);
    const bySection = new Map<string, ClassifiedCommit[]>();

    const breaking = classified.filter(c => c.isBreaking);
    if (breaking.length > 0) bySection.set('Breaking Changes', breaking);

    for (const cc of classified) {
      if (cc.isBreaking) continue;
      const section = CC_TYPE_TO_SECTION[cc.type] || 'Changed';
      if (!bySection.has(section)) bySection.set(section, []);
      bySection.get(section)!.push(cc);
    }

    for (const section of SECTION_ORDER) {
      const items = bySection.get(section);
      if (!items?.length) continue;
      lines.push(`### ${section}`, '');
      for (const item of items) {
        const scopeStr = item.scope ? `**${item.scope}**: ` : '';
        const breakingStr = item.isBreaking ? '⚠️ ' : '';
        lines.push(`- ${breakingStr}${scopeStr}${item.subject} (\`${item.commit.hash.substring(0, 7)}\` by ${item.commit.author})`);
      }
      lines.push('');
    }
  }

  return {
    markdown: lines.join('\n'),
    commitCount: sorted.length,
    versionCount: weeks.length,
    generatedAt: new Date().toISOString(),
  };
}

// ==================== Gap Detection ====================

export function detectChangelogGaps(changelogContent: string, commits: Commit[]): ChangelogGap[] {
  if (!changelogContent || commits.length === 0) return [];

  const contentLower = changelogContent.toLowerCase();
  const gaps: ChangelogGap[] = [];

  // Oldest date in changelog — skip commits before this
  const dateMatches = [...changelogContent.matchAll(/##\s+(\d{4}-\d{2}-\d{2})/g)];
  const oldestDate = dateMatches.length > 0
    ? new Date(Math.min(...dateMatches.map(m => new Date(m[1]).getTime())))
    : null;

  const relevant = commits.filter(c => {
    if (c.isMerge || c.message.toLowerCase().startsWith('merge ')) return false;
    if (oldestDate && new Date(c.date) < oldestDate) return false;
    return true;
  });

  for (const commit of relevant) {
    const shortHash = commit.hash.substring(0, 7).toLowerCase();
    const msgSnippet = commit.message.toLowerCase().trim().substring(0, 40);

    const hashMentioned = contentLower.includes(shortHash) || contentLower.includes(commit.hash.toLowerCase());
    const messageMentioned = msgSnippet.length > 10 && contentLower.includes(msgSnippet);

    if (!hashMentioned && !messageMentioned) {
      const cc = classifyCommit(commit);
      const scopeStr = cc.scope ? `**${cc.scope}**: ` : '';
      gaps.push({
        commit,
        reason: 'not_mentioned',
        suggestedSection: CC_TYPE_TO_SECTION[cc.type] || 'Changed',
        suggestedEntry: `- ${scopeStr}${cc.subject} (\`${shortHash}\` by ${commit.author})`,
      });
    }
  }

  return gaps;
}

// ==================== Health Score ====================

export function calculateHealthScore(changelogContent: string, commits: Commit[], gaps: ChangelogGap[]): ChangelogHealthScore {
  if (!changelogContent) {
    return { score: 0, grade: 'F', coverage: 0, recency: 0, structure: 0, consistency: 0, summary: 'No changelog found.' };
  }

  const relevant = commits.filter(c => !c.isMerge && !c.message.toLowerCase().startsWith('merge '));
  const coverage = relevant.length > 0 ? Math.max(0, 1 - gaps.length / relevant.length) : 1;

  const lastCommitDate = relevant.length > 0
    ? new Date(Math.max(...relevant.map(c => new Date(c.date).getTime())))
    : null;
  const newestEntry = changelogContent.match(/##\s+(\d{4}-\d{2}-\d{2})/);
  const newestEntryDate = newestEntry ? new Date(newestEntry[1]) : null;

  let recency = 0;
  if (lastCommitDate && newestEntryDate) {
    const diffDays = (lastCommitDate.getTime() - newestEntryDate.getTime()) / 86_400_000;
    if (diffDays <= 7) recency = 1;
    else if (diffDays <= 30) recency = 0.7;
    else if (diffDays <= 90) recency = 0.4;
    else recency = 0.1;
  }

  const structure = /^##\s+/m.test(changelogContent) ? (/^###\s+/m.test(changelogContent) ? 1 : 0.6) : 0.2;

  const standardSections = ['Added', 'Fixed', 'Changed', 'Removed', 'Security', 'Deprecated'];
  const matched = standardSections.filter(s => new RegExp(`###\\s+${s}`, 'i').test(changelogContent)).length;
  const consistency = matched >= 3 ? 1 : matched >= 1 ? 0.6 : 0.2;

  const score = Math.round((coverage * 0.4 + recency * 0.3 + structure * 0.15 + consistency * 0.15) * 100);
  const grade: ChangelogHealthScore['grade'] = score >= 90 ? 'A' : score >= 75 ? 'B' : score >= 60 ? 'C' : score >= 40 ? 'D' : 'F';

  const summaries: Record<ChangelogHealthScore['grade'], string> = {
    A: 'Excellent — changelog is well-maintained and comprehensive.',
    B: 'Good — mostly up to date with minor gaps.',
    C: 'Fair — exists but has notable gaps or is slightly stale.',
    D: 'Poor — significantly out of date or poorly structured.',
    F: 'Critical — changelog is missing or severely neglected.',
  };

  return { score, grade, coverage, recency, structure, consistency, summary: summaries[grade] };
}

// ==================== AI Enrichment ====================

export const TECHNICAL_PROMPT = `You are a technical writer for VeriGit generating a changelog in the EXACT format parsed by the VeriGit ChangelogView renderer.

OUTPUT FORMAT — follow precisely:

## YYYY-MM-DD — Descriptive Release Title

### Added
- **Feature Name**: One-line user-facing description of this specific commit.

### Fixed
- **Bug Name**: One-line description of what was fixed.

### Changed
- **Thing Changed**: One-line description of what changed.

### Removed
- **What**: One-line description of what was removed.

### Security
- **Vulnerability**: One-line description of what was patched.

CRITICAL RULES — ALL ARE MANDATORY:

1. DOCUMENT EVERY SINGLE COMMIT. You MUST produce exactly one bullet point per commit in the list. Do NOT combine, merge, summarise, or skip any commits. If you receive 50 commits you MUST output exactly 50 bullet points.
2. You MUST produce a SEPARATE ## YYYY-MM-DD — Short Thematic Title heading for EACH distinct date provided in the batch. Do NOT combine multiple dates under one ## heading — every date must get its own entry.
3. NEVER use [Unreleased], [version], or any bracket-wrapped text. NEVER use version numbers like v1.2.3.
4. Each commit maps to exactly ONE bullet under exactly ONE section (Added/Fixed/Changed/Removed/Security). Classify based on the commit message prefix or content.
5. Bullet format is strictly: - **Short Title**: one-line plain English description rewritten for a user audience.
6. The ## date heading for each section must exactly match the date shown in the commits grouped under it.
7. Output ONLY the markdown — no preamble, no summary, no version bump notes, no token-limit apologies.
8. Use Australian English spelling.
9. Do NOT skip chores, build changes, or dependency updates — classify them as ### Changed.
10. INTEGRATION EVENTS are completely separate from git commits. For EACH integration source present, create a dedicated section AFTER the git sections using EXACTLY these heading names: ### Jira Changes, ### Linear Changes, ### Confluence Changes, ### Notion Changes, ### Trello Changes, ### Monday Changes, ### Google Docs Changes. NEVER place any integration event under ### Added, ### Fixed, ### Changed, ### Removed, or ### Security — those headings are exclusively for git commits. Each integration bullet: - **Issue/Item Key or Title**: one-line plain English description of what changed.`;

export const MARKETING_PROMPT = `You are a marketing copywriter for VeriGit creating a press-friendly product update in the EXACT format parsed by the VeriGit ChangelogView renderer.

OUTPUT FORMAT — follow precisely:

## YYYY-MM-DD — Exciting Benefit-Focused Headline

### What's New
- **Headline Feature**: One punchy sentence describing the user benefit — what they can NOW do that they couldn't before.

### Improvements
- **Area Improved**: One sentence on how the experience is faster, smoother, or more reliable.

### Under the Hood
- **Technical Win**: One plain-English sentence on infrastructure or reliability improvements users will notice.

CRITICAL RULES — ALL ARE MANDATORY:

1. DOCUMENT EVERY SINGLE COMMIT. You MUST produce exactly one bullet point per commit. Do NOT combine, merge, summarise, or skip commits.
2. You MUST produce a SEPARATE ## YYYY-MM-DD — Catchy Benefit Headline for EACH distinct date in the batch. Do NOT combine dates. Every date gets its own ## entry.
3. NEVER use [Unreleased], [version], or version numbers like v1.2.3.
4. Write from the USER's perspective — what they experience, not what the developer changed. No jargon, no internal ticket references, no file names.
5. Map every commit to exactly ONE bullet under What's New, Improvements, or Under the Hood.
6. Bullet format: - **Short Headline**: Exciting one-sentence user benefit.
7. Use active, present-tense voice: "You can now...", "Reports load...", "Searching is..."
8. The ## date heading for each section must exactly match the date of the commits grouped under it.
9. Output ONLY the markdown — no preamble, no commentary.
10. Use Australian English spelling.
11. INTEGRATION EVENTS are completely separate from git commits. For EACH integration source present, create a dedicated section AFTER the git sections using EXACTLY these heading names: ### Jira Changes, ### Linear Changes, ### Confluence Changes, ### Notion Changes, ### Trello Changes, ### Monday Changes, ### Google Docs Changes. NEVER place any integration event under ### What's New, ### Improvements, or ### Under the Hood — those headings are exclusively for git commits. Each integration bullet: - **Issue/Item Key or Title**: one-line plain English description.`;

async function callOpenAI(apiKey: string, model: string, system: string, user: string, onChunk?: (c: string) => void): Promise<string> {
  const resp = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({ model, stream: !!onChunk, messages: [{ role: 'system', content: system }, { role: 'user', content: user }], max_tokens: 8192 }),
  });
  if (!resp.ok) throw new Error(`OpenAI ${resp.status}: ${await resp.text()}`);

  if (!onChunk) {
    const data = await resp.json();
    return data.choices?.[0]?.message?.content || '';
  }

  const reader = resp.body!.getReader();
  const dec = new TextDecoder();
  let result = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const line of dec.decode(value, { stream: true }).split('\n')) {
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      if (raw === '[DONE]') break;
      try {
        const delta = JSON.parse(raw)?.choices?.[0]?.delta?.content || '';
        if (delta) { result += delta; onChunk(delta); }
      } catch { /* */ }
    }
  }
  return result;
}

async function callAnthropic(apiKey: string, model: string, system: string, user: string, onChunk?: (c: string) => void): Promise<string> {
  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01' },
    body: JSON.stringify({ model, max_tokens: 8192, stream: !!onChunk, system, messages: [{ role: 'user', content: user }] }),
  });
  if (!resp.ok) throw new Error(`Anthropic ${resp.status}: ${await resp.text()}`);

  if (!onChunk) {
    const data = await resp.json();
    return data.content?.[0]?.text || '';
  }

  const reader = resp.body!.getReader();
  const dec = new TextDecoder();
  let result = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const line of dec.decode(value, { stream: true }).split('\n')) {
      if (!line.startsWith('data: ')) continue;
      try {
        const parsed = JSON.parse(line.slice(6).trim());
        if (parsed.type === 'content_block_delta') {
          const delta = parsed.delta?.text || '';
          if (delta) { result += delta; onChunk(delta); }
        }
      } catch { /* */ }
    }
  }
  return result;
}

export const BATCH_SIZE = 50; // kept for external consumers; enrichWithAI now uses date-based batching
/** Number of distinct commit dates processed per AI call. Each date gets its own ## heading. */
export const DATES_PER_BATCH = 7;
/** Max commits included per date (prevents single-day token overflow). */
const MAX_COMMITS_PER_DATE = 25;
/** Max integration events included per AI batch — prevents context-length exceeded errors. */
export const MAX_INTEGRATION_PER_BATCH = 150;
/** Rough chars-per-token estimate; used to stay within model context windows. */
const CHARS_PER_TOKEN = 4;
/** Target budget for the user-message portion of a batch (tokens). Leaves room for system prompt + 8k response in a 128k context window. */
const USER_CONTENT_TOKEN_BUDGET = 90_000;

/** Truncate a string to `max` chars with a trailing ellipsis if needed. */
function truncField(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '\u2026' : s;
}

/** Returns the commits that will actually be sent to AI (non-merge only). */
export function getEnrichableCommits(commits: Commit[]): Commit[] {
  return commits.filter(c => !c.isMerge);
}

async function callAIOnce(
  settings: AiSettings,
  model: string,
  systemPrompt: string,
  userContent: string,
  onChunk?: (c: string) => void,
): Promise<string> {
  if (settings.provider === 'openai') {
    return callOpenAI(settings.apiKey, model, systemPrompt, userContent, onChunk);
  } else if (settings.provider === 'anthropic') {
    return callAnthropic(settings.apiKey, model, systemPrompt, userContent, onChunk);
  } else if (settings.provider === 'custom' && settings.customEndpoint) {
    const resp = await fetch(settings.customEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${settings.apiKey}` },
      body: JSON.stringify({ model, messages: [{ role: 'system', content: systemPrompt }, { role: 'user', content: userContent }], max_tokens: 8192 }),
    });
    if (!resp.ok) throw new Error(`Custom AI ${resp.status}: ${await resp.text()}`);
    const data = await resp.json();
    const text = data.choices?.[0]?.message?.content || '';
    if (onChunk) onChunk(text);
    return text;
  }
  throw new Error('Invalid AI provider configuration');
}

export async function enrichWithAI(
  commits: Commit[],
  settings: AiSettings,
  onChunk?: (chunk: string) => void,
  customPrompt?: string,
  integrationEvents?: ChangelogIntegrationEvent[],
): Promise<AiEnrichmentResult> {
  const model = settings.model || (settings.provider === 'openai' ? 'gpt-4o-mini' : 'claude-3-5-haiku-20241022');
  const systemPrompt = customPrompt || TECHNICAL_PROMPT;

  // Filter merge commits
  const filtered = commits.filter(c => !c.isMerge);

  // Group commits by date so each day gets its own ## heading
  const byDate = new Map<string, Commit[]>();
  for (const c of filtered) {
    const d = c.date.substring(0, 10);
    if (!byDate.has(d)) byDate.set(d, []);
    byDate.get(d)!.push(c);
  }
  const sortedDates = [...byDate.keys()].sort(); // ascending — used for total count in summary
  // Descending order: each batch AND dates within each batch are newest-first
  const sortedDatesDesc = [...sortedDates].reverse();

  // Batch dates: DATES_PER_BATCH days per AI call
  const dateBatches: string[][] = [];
  for (let i = 0; i < sortedDatesDesc.length; i += DATES_PER_BATCH) {
    dateBatches.push(sortedDatesDesc.slice(i, i + DATES_PER_BATCH));
  }

  // Pre-sort integration events once
  const sortedIntegration = (integrationEvents ?? []).slice().sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  const batchResults: string[] = [];

  for (let i = 0; i < dateBatches.length; i++) {
    const dateGroup = dateBatches[i];
    // dateGroup is descending (newest first), so [0] = newest, [last] = oldest
    const batchNewest = dateGroup[0];
    const batchOldest = dateGroup[dateGroup.length - 1];

    if (onChunk) onChunk(`\n\n<!-- Batch ${i + 1} of ${dateBatches.length} -->\n`);

    // Build per-date commit sections so AI knows exactly which date each commit belongs to
    let commitSections = '';
    let totalCommitCount = 0;
    for (const date of dateGroup) {
      const dayCommits = (byDate.get(date) ?? []).slice(0, MAX_COMMITS_PER_DATE);
      if (dayCommits.length === 0) continue;
      commitSections += `\n${date} (${dayCommits.length} commit${dayCommits.length === 1 ? '' : 's'}):\n`;
      commitSections += dayCommits
        .map((c, idx) => `${totalCommitCount + idx + 1}. [${c.hash.substring(0, 7)}] ${truncField(c.message, 200)} (${truncField(c.author, 60)})`)
        .join('\n') + '\n';
      totalCommitCount += dayCommits.length;
    }

    // Filter integration events whose timestamps fall within this batch's exact date window.
    // Date-based batching means batchFrom..batchTo always spans DATES_PER_BATCH days,
    // so events correctly land in the week they actually occurred.
    const rawBatchIntegration = sortedIntegration.filter(e => {
      const d = e.timestamp.substring(0, 10);
      return d >= batchOldest && d <= batchNewest;
    });

    // Sort descending before capping so newest events survive the slice
    const cappedIntegration = [...rawBatchIntegration]
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
      .slice(0, MAX_INTEGRATION_PER_BATCH)
      .map(e => ({
        ...e,
        title: truncField(e.title, 120),
        description: truncField(e.description, 100),
        author: e.author ? truncField(e.author, 50) : undefined,
      }));

    const integrationLineList = cappedIntegration.map(
      (e, idx) => `${totalCommitCount + idx + 1}. [${e.source.toUpperCase()}] ${e.title} \u2014 ${e.description} (${e.author ?? 'unknown'}, ${e.timestamp.substring(0, 10)})`,
    );

    // Token budget safety: drop integration events until we fit
    const baseChars = systemPrompt.length + commitSections.length + 500;
    while (integrationLineList.length > 0) {
      const estimatedTokens = Math.ceil((baseChars + integrationLineList.join('\n').length) / CHARS_PER_TOKEN);
      if (estimatedTokens <= USER_CONTENT_TOKEN_BUDGET) break;
      integrationLineList.pop();
      cappedIntegration.pop();
    }

    const trimmedCount = rawBatchIntegration.length - cappedIntegration.length;

    let userContent = `Batch ${i + 1} of ${dateBatches.length}. Covers ${dateGroup.length} day${dateGroup.length === 1 ? '' : 's'}: ${batchOldest} to ${batchNewest}.
You MUST produce a separate ## YYYY-MM-DD heading for EACH date below. Do NOT combine dates.
Total: ${totalCommitCount} commit${totalCommitCount === 1 ? '' : 's'}${cappedIntegration.length > 0 ? ` + ${cappedIntegration.length} integration events` : ''}${trimmedCount > 0 ? ` (${trimmedCount} integration events trimmed for context)` : ''}.

Commits grouped by date:
${commitSections}`;

    if (cappedIntegration.length > 0) {
      userContent += `\nIntegration events to include (${cappedIntegration.length} total):\n${integrationLineList.join('\n')}`;
    }

    const batchMarkdown = await callAIOnce(settings, model, systemPrompt, userContent, onChunk);
    batchResults.push(batchMarkdown);
  }

  const markdown = dateBatches.length === 1
    ? batchResults[0]
    : `# AI-Enriched Changelog\n\n_Generated from ${filtered.length} commits across ${sortedDates.length} active days._\n\n${batchResults.join('\n\n')}`;

  return { markdown, model, enrichedAt: new Date().toISOString() };
}
